import React, { ReactNode, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import AddPromoCode from '../containers/AddPromoCode';
import AddToPack from '../containers/AddToPack';
import ResetPromoCode from '../containers/ResetPromoCode';
import ResetSelection from '../containers/ResetSelection';

import CriticalError from './CriticalError';
import ResetSession from './ResetSession';

import { getClassNames } from '../../../utils';
import { useAppSelector } from '../hooks';
import { resetPackageVars } from '../redux/sessionSlice';

const Modal = ({
  heading: propHeading,
  onAccept,
  packageLineNumber,
  productionSeasonId,
  setModal,
  text: propText,
  type,
  checkoutUrl,
}: ModalProps) => {
  const dispatch = useDispatch();

  const thisRef = useRef(null);

  const { promoCode } = useAppSelector((state) => state.sessionSlice);

  const isAddPromoCodeType = type === 'add-promo-code';
  const isCriticalErrorType = type === 'critical-error';
  const isResetableErrorType = type === 'resetable-error';
  const isResetPromoCodeType = type === 'reset-promo-code';
  const isResetSelectionType = type === 'reset-selection';
  const isSelectPerformanceType = type === 'select-performance' && productionSeasonId;

  const handleCloseModal = () => {
    if (setModal) {
      document.body.classList.remove('has-modal-opened');

      setModal(<></>);
    } else {
      dispatch(resetPackageVars);
      const modal = document.querySelector('.c-cyop__modal');
      if (modal) {
        modal.classList.add('c-cyop__modal__hide');
      }

      window.location.reload();
    }
  };

  let isCentred = false;
  let heading: string;
  let text: ReactNode;

  switch (type) {
    case 'add-promo-code':
      heading = 'Submit promo code to your package';
      text = '';

      break;
    case 'concert-details':
      heading = propHeading || 'Concert Details';
      text = propText || <p>Sorry, no details available for this concert.</p>;

      break;
    case 'critical-error':
      heading = propHeading || 'An error has occurred.';
      isCentred = true;
      text = propText || (
        <p>
          Please try again later or contact our Box Office for assistance on&nbsp;
          <strong>(02) 8215 4600</strong>
          &nbsp;or&nbsp;
          <strong>info@sydneysymphony.com.</strong>
        </p>
      );

      break;
    case 'reset-selection':
      heading = propHeading || 'You are about to remove all seats from your package';
      isCentred = true;
      text = propText || <p>All selected concerts will be removed if you proceed</p>;

      break;
    case 'reset-promo-code':
      heading = 'You are about to remove your promo code from your package';
      isCentred = true;
      text = <p>The promo code will be removed if you proceed</p>;

      break;
    case 'resetable-error':
      heading = propHeading || 'Your session has expired.';
      isCentred = true;
      text = propText || <p>Please click the button below to reset and reload.</p>;

      break;
    case 'select-performance':
      heading = propHeading || 'Add Concert To Pack';
      text = (
        <div>
          {propText || <p>Please select suitable options below.</p>}
          {promoCode ? (<div className="c-cyop__modal-promo-code-info">Your promo code discount will be applied at the cart.</div>) : null}
        </div>
      );

      break;
    default:
      return <></>;
  }
  return (
    <div className="c-cyop__modal" ref={thisRef}>
      <div className={getClassNames(null, 'c-cyop__modal-content', {
        'c-cyop__modal-content--centred': isCentred,
        'c-cyop__modal-content--error-icon': isCriticalErrorType,
        'c-cyop__modal-content--select-performances': isSelectPerformanceType,
      })}
      >
        <div
          aria-label="close modal"
          className="c-cyop__modal-close-button"
          onClick={handleCloseModal}
          onKeyDown={handleCloseModal}
          role="button"
          tabIndex={0}
        />

        <h3 className="c-cyop__modal-heading">{heading}</h3>
        <div className="c-cyop__modal-text s-cms-content">{text}</div>

        {isAddPromoCodeType
          && (
            <AddPromoCode
              handleCloseModal={handleCloseModal}
              parentRef={thisRef}
              onAccept={onAccept}
            />
          )}
        {isCriticalErrorType
          && <CriticalError handleCloseModal={handleCloseModal} checkoutUrl={checkoutUrl || 'https://syso-tnew-test.tnhs.cloud/cart/details'} />}

        {isResetableErrorType
          && <ResetSession handleCloseModal={handleCloseModal} />}

        {isResetSelectionType && onAccept
          && (
            <ResetSelection
              handleCloseModal={handleCloseModal}
              onAccept={onAccept}
              packageLineNumber={packageLineNumber}
            />
          )}

        {isResetPromoCodeType
          && (
            <ResetPromoCode
              handleCloseModal={handleCloseModal}
              parentRef={thisRef}
            />
          )}

        {isSelectPerformanceType
          && (
            <AddToPack
              handleCloseModal={handleCloseModal}
              parentRef={thisRef}
              productionSeasonId={productionSeasonId}
            />
          )}
      </div>
    </div>
  );
};

Modal.defaultProps = {
  heading: '',
  onAccept: undefined,
  packageLineNumber: undefined,
  productionSeasonId: undefined,
  setModal: undefined,
  text: <></>,
  checkoutUrl: '',
};

const modalPropTypes = {
  heading: PropTypes.string,
  onAccept: PropTypes.func,
  packageLineNumber: PropTypes.number,
  productionSeasonId: PropTypes.number,
  setModal: PropTypes.func,
  text: PropTypes.element,
  type: PropTypes.oneOf(['add-promo-code', 'concert-details', 'critical-error', 'reset-selection', 'resetable-error', 'reset-promo-code', 'select-performance']).isRequired,
  checkoutUrl: PropTypes.string,
};

Modal.propTypes = modalPropTypes;

type ModalProps = PropTypes.InferProps<typeof modalPropTypes>;

export default Modal;
